import { ApolloQueryResult } from '@apollo/client';
import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { clearError } from '@app/redux/error';
import { selectUserInfo } from '@app/redux/userInfo';
import { waitForUserInfo } from '@app/redux/utils';
import { getShortenedZipCode } from '@app/utils';

import {
  Address,
  AvailablePhoneNumber,
  ConfigureAddOnFailureReason,
  ConfigureAddonInput,
  EicMessageInput,
  GetCurrentAddOnsPayload,
  PurchaseAddOnInput,
  Query,
  RemoveAddOnInput,
} from '@mfe/shared/schema-types';

import {
  FetchWithErrorsMutation,
  FetchWithErrorsQuery,
  graphqlMutationWithErrors,
  graphqlQueryWithErrors,
} from '../graphql';
import { ScrubAddressModalType } from '../scrubbedAddress';
import {
  CONFIGURE_ADDON,
  GET_ADDON_OFFERS,
  GET_AVAILABLE_PHONE_NUMBERS,
  GET_CURRENT_ADD_ONS,
  GET_EIC_MESSAGES,
  GET_SCRUBBED_ADDRESS,
  PURCHASE_ADD_ON,
  REMOVE_ADDON,
  RESERVE_PHONE_NUMBER,
} from './queriesAndMutations';
import {
  AddOnsState,
  configureAddon,
  getAddOnOffers,
  getAvailableNumbers,
  getCurrentAddOns,
  getEicMessage,
  purchaseAddOn,
  refetchAddOnOffers,
  refetchCurrentAddOns,
  removeAddOn,
  reserveSelectedNumber,
  resetE911ScrubbedAddress,
  scrubEmergencyAddress,
  selectAddOns,
  setAddOnOffers,
  setAddOnOffersError,
  setAddOnOrderId,
  setAvailableNumbers,
  setAvailableNumbersError,
  setConfigureAddonError,
  setConfigureAddonSuccess,
  setCurrentAddOns,
  setE911Error,
  setE911ScrubbedAddress,
  setEicMessage,
  setEicMessageError,
  setEicMessageSuccess,
  setError,
  setLoading,
  setNumberReservationError,
  setNumberReservationSuccess,
  setPurchaseAddOnError,
  setPurchaseAddOnSuccess,
  setShowModal,
  setVoiceEquipmentError,
} from './slice';
import { AddonManagementModalType, sortProductTypesByDisplayOrder } from './utils';
import {
  ScrubAddressProcessStatus,
  ScrubAddressRecommendation,
} from '../../../../platform/src/server/dataSources/PAL/types';

function* fetchAddOnOffers(action: any) {
  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_ADDON_OFFERS,
    variables: {},
    fetchPolicy: action.payload ? 'network-only' : 'cache-first',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors || !data) {
    yield put(setAddOnOffersError(runtimeError ?? errors));
    return;
  }

  const addons = data.getAddOnOffers;

  if (addons) {
    const sortedRootOffers = addons.slice().sort(sortProductTypesByDisplayOrder);

    const sortedOffers = sortedRootOffers.map((addon) => {
      if (addon.is_grouped_product && addon.package_types?.[0]?.product_types) {
        const sortedSubproducts = addon.package_types[0].product_types.slice().sort(sortProductTypesByDisplayOrder);

        return {
          ...addon,
          package_types: [
            { ...addon.package_types[0], product_types: sortedSubproducts },
            ...addon.package_types.slice(1),
          ],
        };
      }

      return addon;
    });

    yield put(setAddOnOffers(sortedOffers));
  }
}

function* fetchAvailablePhoneNumbers(action: PayloadAction<string>) {
  yield call(waitForUserInfo);
  const { userInfo } = yield select(selectUserInfo);

  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_AVAILABLE_PHONE_NUMBERS,
    variables: {
      input: {
        postal_code: getShortenedZipCode(action.payload || userInfo.address.service?.postalCode),
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setAvailableNumbersError(runtimeError ?? errors));
    return;
  }

  if (data?.getAvailableTelephoneNumbers) {
    yield put(setAvailableNumbers(data.getAvailableTelephoneNumbers as AvailablePhoneNumber[]));
  }
}

function* removeAddon(action: PayloadAction<RemoveAddOnInput>) {
  try {
    const apiResponse: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: REMOVE_ADDON,
      variables: { input: action.payload },
      fetchPolicy: 'no-cache',
    });

    const { data, errors, runtimeError } = apiResponse;
    if (data?.removeAddOn?.message || runtimeError || errors) {
      yield put(setShowModal(AddonManagementModalType.CancelSubscriptionError));
      yield put(setLoading(false));
    }

    if (data?.removeAddOn?.orderId) {
      yield put(refetchCurrentAddOns(data.removeAddOn.orderId));
    }
  } catch (e: any) {
    yield put(setShowModal(AddonManagementModalType.CancelSubscriptionError));
    yield put(setLoading(false));
  }
}

function* reserveNumber(action: PayloadAction<string>) {
  const apiResponse: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: RESERVE_PHONE_NUMBER,
    variables: { input: { telephoneNumber: action.payload } },
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setNumberReservationError(runtimeError ?? errors));
    return;
  }

  if (data?.reserveTelephoneNumber) {
    yield put(setNumberReservationSuccess());
  }
}

export const parseE911ScrubbedAddress = (
  data: Partial<ApolloQueryResult<Query>['data']>,
  secondAddressLine: string
): AddOnsState['scrubbedE911Address'] | null => {
  const rawAddress = data.getScrubbedAddress;
  const firstAddressLine = rawAddress?.addressLines[0] ?? '';
  const municipality = rawAddress?.municipality ?? '';
  const region = rawAddress?.region ?? '';
  const postalCode = rawAddress?.postalCode ?? '';
  const countryName = rawAddress?.country ?? '';

  let formattedAddressLine1 = '';
  let formattedAddressLine2 = '';

  if (rawAddress?.processStatus !== ScrubAddressProcessStatus.Incorrect) {
    formattedAddressLine1 = `${firstAddressLine}, ${secondAddressLine} ${municipality}, `;
    formattedAddressLine2 = `${region} ${postalCode} ${countryName}`;
  }

  return {
    rawAddress,
    formattedAddress: { formattedAddressLine1, formattedAddressLine2 },
    processStatus: rawAddress?.processStatus as ScrubAddressProcessStatus,
    recommendation: rawAddress?.recommendation as ScrubAddressRecommendation,
  };
};

export function* scrubEmergencyAddressSaga({ payload }: { type: string; payload: Address }) {
  yield put(clearError({}));

  payload.countryCode = payload.countryCode || 'US';

  try {
    const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_SCRUBBED_ADDRESS,
      variables: { address: payload },
    });

    const { data, errors, runtimeError } = apiResponse;

    if (runtimeError || errors) {
      yield put(setE911Error(runtimeError ?? errors));
      yield put(setShowModal(ScrubAddressModalType.AddressError));
      return;
    }
    let scrubbedE911Address;
    if (data?.getScrubbedAddress) {
      const secondAddressLine = payload.addressLines[1];
      scrubbedE911Address = parseE911ScrubbedAddress(data, secondAddressLine);
    }

    yield put(resetE911ScrubbedAddress());

    if (scrubbedE911Address) {
      yield put(setE911ScrubbedAddress({ scrubbedE911Address }));
    } else {
      yield put(
        setE911Error({
          operation: 'getScrubbedAddress',
          message: ['E911 getScrubbedAddress attempt failed.'],
        })
      );
    }
  } catch (error) {
    yield put(
      setE911Error({
        operation: 'getScrubbedAddress',
        message: ['E911 getScrubbedAddress attempt failed.'],
      })
    );
    yield put(resetE911ScrubbedAddress());
    yield put(setShowModal(ScrubAddressModalType.AddressError));
  }
}

type PayloadType = {
  scrubbedE911Address: AddOnsState['scrubbedE911Address'];
};

function* checkScrubProcessStatus({ payload }: { type: string; payload: PayloadType }) {
  const { processStatus, recommendation } = payload.scrubbedE911Address;

  if (
    processStatus === ScrubAddressProcessStatus.Verified ||
    (processStatus === ScrubAddressProcessStatus.Corrected && recommendation === ScrubAddressRecommendation.RECOMMEND)
  ) {
    yield put(setShowModal(null));
    return;
  }

  if (processStatus === ScrubAddressProcessStatus.Incorrect && recommendation === ScrubAddressRecommendation.REJECT) {
    yield put(setShowModal(ScrubAddressModalType.IncorrectAddress));
    return;
  }

  if (processStatus === ScrubAddressProcessStatus.Corrected) {
    return;
  }
  yield put(setShowModal(ScrubAddressModalType.AddressError));
}

function* configureAddonSaga(action: PayloadAction<ConfigureAddonInput>) {
  const input = action.payload;

  const apiResponse: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: CONFIGURE_ADDON,
    variables: { input },
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setConfigureAddonError(runtimeError ?? errors));
  }

  if (data && data.configureAddon) {
    yield put(setConfigureAddonSuccess(data.configureAddon.success));

    if (!data?.configureAddon?.success && data?.configureAddon?.message === ConfigureAddOnFailureReason.ExtendCart) {
      yield put(setConfigureAddonError({ message: 'Unable to extend cart' }));
    }
    if (
      !data?.configureAddon?.success &&
      data?.configureAddon?.message === ConfigureAddOnFailureReason.VoiceEquipment
    ) {
      yield put(setVoiceEquipmentError(true));
    }
  } else {
    yield put(setConfigureAddonError({ message: 'Unable to configure addon' }));
  }
}

function* purchaseAddOnSaga(action: PayloadAction<PurchaseAddOnInput>) {
  const input = action.payload;
  const { configureAddonSuccess } = yield select(selectAddOns);

  try {
    if (!configureAddonSuccess || input.voice) {
      // if there were disclosures to be shown, we would configure our cart on the Disclosures page
      //since there are none to display, we want to make sure we still configure our cart before submitting the order
      yield call(configureAddonSaga, action);
    }

    const { voiceEquipmentError, configureAddonError } = yield select(selectAddOns);

    if (voiceEquipmentError || configureAddonError) {
      yield put(setPurchaseAddOnError('Unable to configure addon'));
      return;
    }

    const apiResponse: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: PURCHASE_ADD_ON,
      variables: { input: action.payload },
    });

    const { data } = apiResponse;

    if (data?.purchaseAddOn) {
      yield put(setPurchaseAddOnSuccess(true));
      yield put(setAddOnOrderId(data.purchaseAddOn.orderId ?? null));
      yield call(
        [sessionStorageManager, sessionStorageManager.setItem],
        SESSION_STORAGE_VARIABLES.PURCHASED_PRODUCT_TYPE_ID,
        data.purchaseAddOn.rootProductTypeId
      );
    } else {
      yield put(setPurchaseAddOnError('Operation unsuccessful'));
    }
  } catch (error) {
    yield put(setPurchaseAddOnError(error));
  }
}

function* fetchCurrentAddons(action: any) {
  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_CURRENT_ADD_ONS,
    fetchPolicy: 'no-cache',
    variables: { input: { addOnOrderId: action.payload } },
  });

  const { data, errors, runtimeError } = apiResponse;

  if (runtimeError || errors) {
    yield put(setError(runtimeError ?? errors));
    return;
  }

  if (data?.getCurrentAddOns) {
    yield put(setCurrentAddOns(data.getCurrentAddOns as GetCurrentAddOnsPayload[]));
  }
}

function* getEicMessageSaga(action: PayloadAction<EicMessageInput>) {
  const input = action.payload;
  try {
    const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_EIC_MESSAGES,
      variables: { input },
      fetchPolicy: 'no-cache',
    });

    const { data, errors, runtimeError } = apiResponse;

    if (runtimeError || errors) {
      yield put(setConfigureAddonError(runtimeError ?? errors));
    }

    if ((data?.getEicMessage?.eicMessage?.length ?? 0) > 0) {
      yield put(setEicMessage(data?.getEicMessage));
      yield put(setEicMessageSuccess(true));
    } else {
      yield put(setEicMessageError('Unable to get EIC message'));
      yield put(setEicMessageSuccess(false));
    }
  } catch (error) {
    yield put(setEicMessageError(error));
  }
}

function* refetchCurrentAddOnsSaga(action: any) {
  yield call(fetchCurrentAddons, action);
}

function* refetchAddOnOffersSaga() {
  yield call(fetchAddOnOffers, {
    type: 'refetchAddOnOffers',
    payload: { refetchData: true },
  });
}

export function* watchAddOns() {
  yield all([
    takeLatest(getAvailableNumbers.type, fetchAvailablePhoneNumbers),
    takeLatest(reserveSelectedNumber.type, reserveNumber),
    takeLatest(scrubEmergencyAddress.type, scrubEmergencyAddressSaga),
    takeLatest(setE911ScrubbedAddress.type, checkScrubProcessStatus),
    takeLatest(purchaseAddOn.type, purchaseAddOnSaga),
    takeLatest(configureAddon.type, configureAddonSaga),
    takeLatest(getEicMessage.type, getEicMessageSaga),
    takeLatest(removeAddOn.type, removeAddon),
    takeLatest(getAddOnOffers.type, fetchAddOnOffers),
    takeLatest(refetchAddOnOffers.type, refetchAddOnOffersSaga),
    takeLatest(getCurrentAddOns.type, fetchCurrentAddons),
    takeLatest(refetchCurrentAddOns.type, refetchCurrentAddOnsSaga),
  ]);
}
